import confetti from "canvas-confetti";

/**
 * Shared confetti utilities. Use directly anywhere (e.g. import { playConfetti } from '@/utils/confetti').
 * Vue components can use the confettiMixin for this.playConfetti() / this.playConfettiSimple() instead.
 */

/** Brand color palettes for confetti */
const CONFETTI_COLORS = [
  ["#0dace9", "#0394da", "#56c4f9", "#3fe6ff"], // blues
  ["#a1d86f", "#69c69d", "#65c733", "#0fe283"], // greens
  ["#720fe2", "#9440f2", "#8822df", "#6a029f"], // purples
  ["#c101f1", "#e627f7", "#c500a2", "#ec4ed0"], // pinks
  ["#f1cc00", "#ffda6b", "#fac129", "#ffdc1b"], // yellow/orange
  ["#f1cc00", "#e627f7", "#6a029f", "#a1d86f", "#3fe6ff"], // rainbow
];

const DEFAULT_Z_INDEX = 999;

/** Hardcoded origins for playConfettiSimple: left, right, center (burst order) */
const SIMPLE_ORIGIN_LEFT = { y: 0.95, x: 0.3 };
const SIMPLE_ORIGIN_CENTER = { y: 0.95, x: 0.5 };
const SIMPLE_ORIGIN_RIGHT = { y: 0.95, x: 0.7 };

/**
 * Play the full confetti sequence with optional custom colors.
 * @param {Object} options
 * @param {'normal'|'special'} [options.type='normal'] - 'normal' uses a random palette; 'special' uses rainbow + extra bursts
 * @param {number} [options.zIndex=999]
 * @returns {Promise<void>}
 */
export async function playConfetti(options = {}) {
  const { type = "normal", zIndex = DEFAULT_Z_INDEX } = options;
  const baseOpts = { zIndex };

  const randColor =
    type !== "special"
      ? CONFETTI_COLORS[Math.floor(Math.random() * (CONFETTI_COLORS.length - 2))]
      : CONFETTI_COLORS[5];

  confetti({
    particleCount: 300,
    spread: 70,
    scalar: 1.13,
    colors: randColor,
    origin: { y: 0.9, x: 0.56 },
    ...baseOpts,
  });
  confetti({
    particleCount: 120,
    spread: 200,
    decay: 0.91,
    scalar: 0.8,
    colors: randColor,
    origin: { y: 0.8, x: 0.52 },
    ...baseOpts,
  });
  confetti({
    particleCount: 150,
    spread: 120,
    startVelocity: 45,
    scalar: 0.8,
    colors: randColor,
    origin: { y: 0.6, x: 0.54 },
    ...baseOpts,
  });

  if (type === "special") {
    await new Promise((resolve) => setTimeout(resolve, 300));

    confetti({
      particleCount: 80,
      spread: 120,
      startVelocity: 25,
      scalar: 0.7,
      colors: CONFETTI_COLORS[0],
      origin: { y: 0.3, x: 0.2 },
      ...baseOpts,
    });
    confetti({
      particleCount: 80,
      spread: 120,
      startVelocity: 25,
      scalar: 0.7,
      colors: CONFETTI_COLORS[1],
      origin: { y: 0.2, x: 0.8 },
      ...baseOpts,
    });
    await new Promise((resolve) => setTimeout(resolve, 200));
    confetti({
      particleCount: 80,
      spread: 120,
      startVelocity: 25,
      scalar: 0.7,
      colors: CONFETTI_COLORS[1],
      origin: { y: 0.74, x: 0.2 },
      ...baseOpts,
    });
    confetti({
      particleCount: 80,
      spread: 120,
      startVelocity: 25,
      scalar: 0.7,
      colors: CONFETTI_COLORS[0],
      origin: { y: 0.74, x: 0.8 },
      ...baseOpts,
    });
  }
}

/**
 * Play a simple 3-burst confetti (no custom colors).
 * Uses hardcoded left/center/right origins so bursts are visibly spread.
 * When staggered: same 3 bursts (left, right, center) with delays between them (0ms, 400ms, 700ms).
 * @param {Object} [options]
 * @param {number} [options.zIndex=999]
 * @param {boolean} [options.staggered] - if true, delay 2nd and 3rd bursts by 400ms and 700ms; if false, all 3 fire at once
 */
export function playConfettiSimple(options = {}) {
  const { zIndex = DEFAULT_Z_INDEX, staggered = false } = options;

  const burst1 = () =>
    confetti({
      particleCount: 300,
      spread: 70,
      scalar: 1.13,
      origin: SIMPLE_ORIGIN_LEFT,
      zIndex,
    });
  const burst2 = () =>
    confetti({
      particleCount: 200,
      spread: 100,
      decay: 0.91,
      scalar: 0.8,
      origin: SIMPLE_ORIGIN_RIGHT,
      zIndex,
    });
  const burst3 = () =>
    confetti({
      particleCount: 100,
      spread: 120,
      startVelocity: 45,
      scalar: 0.8,
      origin: SIMPLE_ORIGIN_CENTER,
      zIndex,
    });

  if (staggered) {
    burst1();
    setTimeout(burst2, 400);
    setTimeout(burst3, 700);
  } else {
    burst1();
    burst2();
    burst3();
  }
}
